// src/App.js
import React, { useState } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import jsPDF from "jspdf";
import "./styles.css";

// Chart.js setup
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function App() {
  // ─────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────
  const [physicalInput, setPhysicalInput] = useState({
    budget: "0",
    frequency: "0",
    costPerEvent: "0",
    marketingROI: "0",
    adRevenue: "0",
    churnRate: "0",
    ltv: "0",
    mediaRights: "0",
    scaleFactor: "1",
    seasonalityModifier: "0",
    infraAmort: "0",
    estimatedPlayers: "0",
    sponsorshipRevenue: "0",
    ticketMerchRevenue: "0",
    licenseFeePerEvent: "0",
    licenseFeeFlat: "0",
    franchiseFee: "0",
  });
  const [digitalInput, setDigitalInput] = useState({ ...physicalInput });
  const [showInputModal, setShowInputModal] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showEventForecast, setShowEventForecast] = useState(false);
  const [showRiskSim, setShowRiskSim] = useState(false);
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const [sensitivity, setSensitivity] = useState("normal");
  const [eventType, setEventType] = useState("both");
  const [eventForecast, setEventForecast] = useState({
    eventsPerYear: "5",
    baseRevenuePerEvent: "10000",
    growthRate: "10",
  });

  // ─── Marketing ROI Calculator state ─────────────────────────────
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcSpend, setCalcSpend] = useState("0");
  const [calcRevenue, setCalcRevenue] = useState("0");

  // Scenario & Compare
  const [scenarios, setScenarios] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState("");
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparePair, setComparePair] = useState(["", ""]);
  const [comparisonResults, setComparisonResults] = useState(null);

  // Risk sim results
  const [riskResults, setRiskResults] = useState({
    profits: [],
    summary: null,
  });

  // ─────────────────────────────────────────────────────────────────
  // Helpers & Calculations
  // ─────────────────────────────────────────────────────────────────
  const sensitivityFactor = (field, value) => {
    const m = sensitivity === "+10%" ? 1.1 : sensitivity === "-10%" ? 0.9 : 1;
    return ["adRevenue", "costPerEvent", "marketingROI", "churnRate"].includes(
      field
    )
      ? value * m
      : value;
  };

  function toNum(str) {
    const n = parseFloat(str);
    return isNaN(n) ? 0 : n;
  }

  const calcMetrics = (ev) => {
    const freq = toNum(ev.frequency);
    const costPer = sensitivityFactor("costPerEvent", toNum(ev.costPerEvent));
    const adRev = sensitivityFactor("adRevenue", toNum(ev.adRevenue));
    const mROI = sensitivityFactor("marketingROI", toNum(ev.marketingROI));
    const churn = sensitivityFactor("churnRate", toNum(ev.churnRate));

    const recurringCosts =
      (costPer + toNum(ev.infraAmort) + toNum(ev.licenseFeePerEvent)) * freq;
    const totalCost =
      toNum(ev.budget) +
      toNum(ev.licenseFeeFlat) +
      toNum(ev.franchiseFee) +
      recurringCosts;

    const mReturn = (toNum(ev.budget) * mROI) / 100;
    const ltvRevenue =
      toNum(ev.estimatedPlayers) * (1 - churn / 100) * toNum(ev.ltv);
    const recurringRevenue =
      (adRev +
        toNum(ev.mediaRights) +
        toNum(ev.sponsorshipRevenue) +
        toNum(ev.ticketMerchRevenue)) *
      freq;

    const totalRevenue =
      (recurringRevenue + mReturn + ltvRevenue) *
      (1 + toNum(ev.seasonalityModifier) / 100) *
      toNum(ev.scaleFactor);

    const costEfficiency = totalRevenue / totalCost;
    const estNewUsers =
      toNum(ev.ltv) > 0 ? (toNum(ev.budget) * mROI) / 100 / toNum(ev.ltv) : 0;
    const revenuePerEvent = freq ? totalRevenue / freq : 0;
    const breakEvenEvents =
      revenuePerEvent > 0 ? totalCost / revenuePerEvent : Infinity;

    return {
      totalCost,
      totalRevenue,
      profit: totalRevenue - totalCost,
      costEfficiency,
      estNewUsers,
      breakEvenEvents,
      mReturn,
      ltvRevenue,
      recurringRevenue,
    };
  };

  // Aggregate over physical/digital
  const metrics = ["physical", "digital"].reduce(
    (acc, type) => {
      if (eventType === type || eventType === "both") {
        const st = type === "physical" ? physicalInput : digitalInput;
        const m = calcMetrics(st);
        acc.totalCost += m.totalCost;
        acc.totalRevenue += m.totalRevenue;
        acc.profit += m.profit;
        acc.estNewUsers += m.estNewUsers;
        acc.breakEvenEvents.push(m.breakEvenEvents);
        acc.costEfficiencies.push(m.costEfficiency);
        acc.playerRevenue += m.ltvRevenue;
        acc.marketingRevenue += m.mReturn;
        acc.recurringRevenue += m.recurringRevenue;
      }
      return acc;
    },
    {
      totalCost: 0,
      totalRevenue: 0,
      profit: 0,
      estNewUsers: 0,
      breakEvenEvents: [],
      costEfficiencies: [],
      playerRevenue: 0,
      marketingRevenue: 0,
      recurringRevenue: 0,
    }
  );
  const isProfitable = metrics.profit > 0;

  // Scenario Handlers
  const handleSaveScenario = () => {
    if (!newScenarioName.trim()) return;
    setScenarios([
      ...scenarios,
      {
        name: newScenarioName.trim(),
        metrics: { ...metrics },
        forecast: { ...eventForecast },
      },
    ]);
    setNewScenarioName("");
    setShowSaveModal(false);
  };
  const handleCompare = () => {
    const a = scenarios.find((s) => s.name === comparePair[0]);
    const b = scenarios.find((s) => s.name === comparePair[1]);
    if (!a || !b || a.name === b.name) return;
    setComparisonResults({ a, b });
    setShowCompareModal(false);
  };

  // Monte Carlo Risk Simulation
  const runRiskSim = (runs = 1000) => {
    const profits = [];
    for (let i = 0; i < runs; i++) {
      const samp = (val) => val * (1 + (Math.random() * 2 - 1) * 0.1);
      const phys = {
        ...physicalInput,
        marketingROI: samp(toNum(physicalInput.marketingROI)),
        churnRate: samp(toNum(physicalInput.churnRate)),
        adRevenue: samp(toNum(physicalInput.adRevenue)),
        costPerEvent: samp(toNum(physicalInput.costPerEvent)),
      };
      const digi = {
        ...digitalInput,
        marketingROI: samp(toNum(digitalInput.marketingROI)),
        churnRate: samp(toNum(digitalInput.churnRate)),
        adRevenue: samp(toNum(digitalInput.adRevenue)),
        costPerEvent: samp(toNum(digitalInput.costPerEvent)),
      };
      const m1 = calcMetrics(phys);
      const m2 = calcMetrics(digi);
      profits.push(m1.profit + m2.profit);
    }
    const mean = profits.reduce((sum, p) => sum + p, 0) / profits.length;
    const std = Math.sqrt(
      profits.reduce((sum, p) => sum + (p - mean) ** 2, 0) / profits.length
    );
    const probLoss =
      (profits.filter((p) => p < 0).length / profits.length) * 100;
    setRiskResults({ profits, summary: { mean, std, probLoss } });
  };

  // PDF Export (all key metrics + risk summary)
  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Esports Profitability Toolkit Summary", 10, 10);
    doc.setFontSize(10);
    let y = 20;
    doc.text(`Total Cost: $${metrics.totalCost.toFixed(2)}`, 10, y);
    y += 6;
    doc.text(`Total Revenue: $${metrics.totalRevenue.toFixed(2)}`, 10, y);
    y += 6;
    doc.text(`Profit: $${metrics.profit.toFixed(2)}`, 10, y);
    y += 6;
    doc.text(
      `Cost Eff.: ${(metrics.totalRevenue / metrics.totalCost).toFixed(2)}×`,
      10,
      y
    );
    y += 6;
    doc.text(`New Users: ${Math.round(metrics.estNewUsers)}`, 10, y);
    y += 6;
    doc.text(
      `Breakeven Evts: ${Math.round(Math.max(...metrics.breakEvenEvents))}`,
      10,
      y
    );
    if (riskResults.summary) {
      y += 10;
      doc.text(
        `Risk Sim → Mean Profit: $${riskResults.summary.mean.toFixed(2)}`,
        10,
        y
      );
      y += 6;
      doc.text(`Std Dev: $${riskResults.summary.std.toFixed(2)}`, 10, y);
      y += 6;
      doc.text(
        `Prob of Loss: ${riskResults.summary.probLoss.toFixed(1)}%`,
        10,
        y
      );
    }
    doc.save("Toolkit_Summary.pdf");
  };

  // Input & Forecast Renderers
  const inputFields = [
    { key: "budget", label: "Initial Budget ($)*" },
    { key: "frequency", label: "Event Frequency (per season)*" },
    { key: "costPerEvent", label: "Cost per Event ($)*" },
    { key: "infraAmort", label: "Infrastructure Amortization ($/event)" },
    { key: "licenseFeePerEvent", label: "License Fee Per Event ($)" },
    { key: "licenseFeeFlat", label: "License Fee Flat ($)" },
    { key: "franchiseFee", label: "Franchise Fee ($)" },
  ];
  const revenueFields = [
    { key: "adRevenue", label: "Ad Revenue ($/event)" },
    { key: "sponsorshipRevenue", label: "Sponsorship Revenue ($/event)" },
    { key: "ticketMerchRevenue", label: "Ticket/Merch Revenue ($/event)" },
    { key: "marketingROI", label: "Marketing ROI (%)" },
    { key: "mediaRights", label: "Media Rights Revenue ($/event)" },
    { key: "estimatedPlayers", label: "Estimated Total Players" },
    { key: "ltv", label: "Player LTV ($)" },
    { key: "churnRate", label: "Churn Rate (%)" },
    { key: "seasonalityModifier", label: "Seasonality Modifier (%)" },
    { key: "scaleFactor", label: "Scale Factor" },
  ];

  const handleChange = (type, field, value) => {
    const setter = type === "physical" ? setPhysicalInput : setDigitalInput;
    setter((curr) => ({ ...curr, [field]: value }));
  };

  const renderSection = (type, state) => (
    <div className="input-section">
      <h2>{type === "physical" ? "Physical" : "Digital"} Events</h2>
      {[...inputFields, ...revenueFields].map((f) => (
        <div key={`${type}-${f.key}`} className="input-row" title={f.label}>
          <label>{f.label}</label>
          <input
            type="number"
            step="any"
            value={state[f.key]}
            onChange={(e) => handleChange(type, f.key, e.target.value)}
          />
        </div>
      ))}
    </div>
  );

  const renderForecast = () => {
    const basePlayers = toNum(physicalInput.estimatedPlayers);
    const baseLTV = toNum(physicalInput.ltv);
    const churn = toNum(physicalInput.churnRate) / 100;
    const yrs = [1, 2, 3];
    return (
      <div className="forecast-panel">
        <h3>📊 3-Year Player LTV Forecast</h3>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Retained Players</th>
              <th>Projected Rev</th>
            </tr>
          </thead>
          <tbody>
            {yrs.map((y) => {
              const retained = basePlayers * Math.pow(1 - churn, y);
              const rev = retained * baseLTV;
              return (
                <tr key={y}>
                  <td>Year {y}</td>
                  <td>{Math.round(retained)}</td>
                  <td>${rev.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: "0.85em", color: "gray" }}>
          * Steady churn & LTV.
        </p>
      </div>
    );
  };

  const renderEventForecast = () => {
    const { eventsPerYear, baseRevenuePerEvent, growthRate } = eventForecast;
    const yrs = [1, 2, 3];
    return (
      <div className="forecast-panel">
        <h3>📈 3-Year Event Revenue Forecast</h3>
        <div className="forecast-inputs">
          <label>Events / Year:</label>
          <input
            type="number"
            step="any"
            value={eventsPerYear}
            onChange={(e) =>
              setEventForecast({
                ...eventForecast,
                eventsPerYear: e.target.value,
              })
            }
          />
          <label>Rev / Event ($):</label>
          <input
            type="number"
            step="any"
            value={baseRevenuePerEvent}
            onChange={(e) =>
              setEventForecast({
                ...eventForecast,
                baseRevenuePerEvent: e.target.value,
              })
            }
          />
          <label>Growth Rate (%):</label>
          <input
            type="number"
            step="any"
            value={growthRate}
            onChange={(e) =>
              setEventForecast({
                ...eventForecast,
                growthRate: e.target.value,
              })
            }
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Events</th>
              <th>Projected Rev</th>
            </tr>
          </thead>
          <tbody>
            {yrs.map((y) => {
              const rev =
                toNum(eventsPerYear) *
                toNum(baseRevenuePerEvent) *
                Math.pow(1 + toNum(growthRate) / 100, y);
              return (
                <tr key={y}>
                  <td>Year {y}</td>
                  <td>{toNum(eventsPerYear)}</td>
                  <td>${rev.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: "0.85em", color: "gray" }}>* Steady growth.</p>
      </div>
    );
  };

  // Charts
  const RevenueBreakdownChart = () => {
    const data = {
      labels: ["Player", "Marketing", "Recurring"],
      datasets: [
        {
          data: [
            metrics.playerRevenue,
            metrics.marketingRevenue,
            metrics.recurringRevenue,
          ],
          backgroundColor: ["#4caf50", "#2196f3", "#ff9800"],
        },
      ],
    };
    return (
      <div className="chart-wrapper">
        <h4>Revenue Breakdown</h4>
        <Pie data={data} />
      </div>
    );
  };

  const ForecastBarChart = () => {
    const yrs = [1, 2, 3];
    const data = {
      labels: yrs.map((y) => `Year ${y}`),
      datasets: [
        {
          label: "Event Revenue",
          data: yrs.map(
            (y) =>
              toNum(eventForecast.eventsPerYear) *
              toNum(eventForecast.baseRevenuePerEvent) *
              Math.pow(1 + toNum(eventForecast.growthRate) / 100, y)
          ),
        },
      ],
    };
    return (
      <div className="chart-wrapper">
        <h4>Event Revenue Forecast</h4>
        <Bar data={data} />
      </div>
    );
  };

  // Tornado Sensitivity Chart
  const TornadoChart = () => {
    const labelMap = {
      budget: "Budget",
      frequency: "Frequency",
      costPerEvent: "Cost/Event",
      adRevenue: "Ad Rev",
      marketingROI: "Mktg ROI",
      churnRate: "Churn",
      ltv: "Player LTV",
      mediaRights: "Media Rights",
      sponsorshipRevenue: "Sponsor Rev",
      ticketMerchRevenue: "Merch Rev",
      scaleFactor: "Scale Factor",
      seasonalityModifier: "Season Mod",
      infraAmort: "Infra Amort",
      licenseFeePerEvent: "License/Evt",
      licenseFeeFlat: "License Flat",
      franchiseFee: "Franchise Fee",
    };
    const drivers = Object.keys(labelMap);
    const deltas = drivers.map((drv) => {
      let plusP = 0,
        minusP = 0;
      if (eventType !== "digital") {
        const pH = { ...physicalInput, [drv]: toNum(physicalInput[drv]) * 1.1 };
        plusP += calcMetrics(pH).profit;
        const pL = { ...physicalInput, [drv]: toNum(physicalInput[drv]) * 0.9 };
        minusP += calcMetrics(pL).profit;
      }
      if (eventType !== "physical") {
        const dH = { ...digitalInput, [drv]: toNum(digitalInput[drv]) * 1.1 };
        plusP += calcMetrics(dH).profit;
        const dL = { ...digitalInput, [drv]: toNum(digitalInput[drv]) * 0.9 };
        minusP += calcMetrics(dL).profit;
      }
      return { drv, delta: (plusP - minusP) / 2 };
    });
    deltas.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    const labels = deltas.map((d) => labelMap[d.drv]);
    const data = deltas.map((d) => d.delta);

    return (
      <div className="chart-wrapper">
        <h4>Tornado Sensitivity</h4>
        <Bar
          data={{
            labels,
            datasets: [{ label: "Δ Profit", data, backgroundColor: "#4caf50" }],
          }}
          options={{
            indexAxis: "y",
            plugins: { legend: { display: false } },
            scales: { x: { ticks: { callback: (v) => `$${v.toFixed(0)}` } } },
          }}
        />
      </div>
    );
  };

  // 36-Month Revenue Timeline
  const TimelineChart = () => {
    const { eventsPerYear, baseRevenuePerEvent, growthRate } = eventForecast;
    const months = Array.from({ length: 36 }, (_, i) => i + 1);
    const monthlyRate = Math.pow(1 + toNum(growthRate) / 100, 1 / 12) - 1;
    const dataPts = months.map(
      (m) =>
        (toNum(eventsPerYear) / 12) *
        toNum(baseRevenuePerEvent) *
        Math.pow(1 + monthlyRate, m)
    );
    return (
      <div className="chart-wrapper">
        <h4>36-Month Revenue Timeline</h4>
        <Line
          data={{
            labels: months.map((m) => `M${m}`),
            datasets: [
              {
                label: "Monthly Rev",
                data: dataPts,
                borderColor: "#2196f3",
                fill: false,
              },
            ],
          }}
          options={{
            plugins: {
              tooltip: {
                callbacks: {
                  label: (ctx) => `$${ctx.parsed.y.toFixed(0)}`,
                },
              },
            },
            scales: { y: { ticks: { callback: (v) => `$${v.toFixed(0)}` } } },
          }}
        />
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="App">
      <div className="watermark">
        This Esports Profit-Model Toolkit is an Alpha developed by
        @OrganizedPlayer / Dante Horton for consultancy and research, find me on
        Substack or LinkedIn!
      </div>

      <h1 className="title">Esports Profitability Toolkit</h1>

      {/* Toolbar */}
      <div className="button-group">
        <button onClick={() => setShowInputModal(true)}>
          📘 Explain Inputs
        </button>
        <button
          className={showAssumptions ? "active" : ""}
          onClick={() => setShowAssumptions(!showAssumptions)}
        >
          ℹ️ View Assumptions
        </button>
        <button
          className={showEventForecast ? "active" : ""}
          onClick={() => setShowEventForecast(!showEventForecast)}
        >
          📈 Event Forecast
        </button>
        <button
          className={showRiskSim ? "active" : ""}
          onClick={() => {
            setShowRiskSim(!showRiskSim);
            if (!showRiskSim) runRiskSim();
          }}
        >
          🔀 Risk Sim
        </button>
        <button onClick={() => setShowSaveModal(true)}>💾 Save Scenario</button>
        <button
          className={showCompareModal ? "active" : ""}
          onClick={() => setShowCompareModal(!showCompareModal)}
        >
          🔍 Compare
        </button>
        <button onClick={generatePDF}>📄 Export PDF</button>
        <button
          className={showSensitivity ? "active" : ""}
          onClick={() => setShowSensitivity(!showSensitivity)}
        >
          🗜 Sensitivity
        </button>
        <button
          className={showTimeline ? "active" : ""}
          onClick={() => setShowTimeline(!showTimeline)}
        >
          📆 Timeline
        </button>
        <button onClick={() => setShowCalcModal(true)}>🧮 ROI Calc</button>
      </div>

      {/* Assumptions Modal */}
      {showAssumptions && (
        <div className="modal">
          <div className="modal-content">
            <h2>Toolkit Assumptions</h2>
            <ul>
              <li>No LTV → no player revenue.</li>
              <li>0 ROI → skip new users calc.</li>
              <li>0 churn → full retention.</li>
              <li>No scaleFactor → ×1.</li>
              <li>No frequency → breakeven N/A.</li>
            </ul>
            <button onClick={() => setShowAssumptions(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Save Scenario</h2>
            <input
              type="text"
              placeholder="Scenario Name"
              value={newScenarioName}
              onChange={(e) => setNewScenarioName(e.target.value)}
            />
            <button onClick={handleSaveScenario}>Save</button>
            <button onClick={() => setShowSaveModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {showCompareModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Compare Scenarios</h2>
            <select
              value={comparePair[0]}
              onChange={(e) => setComparePair([e.target.value, comparePair[1]])}
            >
              <option value="">Select A</option>
              {scenarios.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={comparePair[1]}
              onChange={(e) => setComparePair([comparePair[0], e.target.value])}
            >
              <option value="">Select B</option>
              {scenarios.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
            <button onClick={handleCompare}>Compare</button>
            <button onClick={() => setShowCompareModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Marketing ROI Calculator Modal */}
      {showCalcModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Marketing ROI Calculator</h2>
            <div className="input-row">
              <label>Marketing Spend ($):</label>
              <input
                type="number"
                step="any"
                value={calcSpend}
                onChange={(e) => setCalcSpend(e.target.value)}
              />
            </div>
            <div className="input-row">
              <label>Revenue Generated ($):</label>
              <input
                type="number"
                step="any"
                value={calcRevenue}
                onChange={(e) => setCalcRevenue(e.target.value)}
              />
            </div>
            <p>
              <strong>ROI%: </strong>
              {toNum(calcSpend) > 0
                ? (
                    ((toNum(calcRevenue) - toNum(calcSpend)) /
                      toNum(calcSpend)) *
                    100
                  ).toFixed(1) + "%"
                : "—"}
            </p>
            <button onClick={() => setShowCalcModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Input Guide Modal */}
      {showInputModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Input Guide</h2>
            <p>
              Full guide on Google Docs:{" "}
              <a
                href="https://docs.google.com/document/d/1MFXWpxlTQWAJJaZgWZFSH_J-DJLKcUH0QjKLNgOq1YM/edit"
                target="_blank"
                rel="noreferrer"
              >
                https://goo.gl/YourGuideLink
              </a>
            </p>
            <button onClick={() => setShowInputModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Type Toggles */}
      <div className="button-group">
        <button
          className={eventType === "physical" ? "active" : ""}
          onClick={() => setEventType("physical")}
        >
          Physical Only
        </button>
        <button
          className={eventType === "digital" ? "active" : ""}
          onClick={() => setEventType("digital")}
        >
          Digital Only
        </button>
        <button
          className={eventType === "both" ? "active" : ""}
          onClick={() => setEventType("both")}
        >
          Both
        </button>
      </div>

      {/* Sensitivity Toggles */}
      <div className="button-group sensitivity">
        <button
          className={`toggle ${sensitivity === "normal" ? "active" : ""}`}
          onClick={() => setSensitivity("normal")}
        >
          Normal
        </button>
        <button
          className={`toggle ${sensitivity === "+10%" ? "active" : ""}`}
          onClick={() => setSensitivity("+10%")}
        >
          +10% ✅
        </button>
        <button
          className={`toggle ${sensitivity === "-10%" ? "active" : ""}`}
          onClick={() => setSensitivity("-10%")}
        >
          -10%
        </button>
      </div>

      {/* Parameter Sections */}
      {(eventType === "physical" || eventType === "both") &&
        renderSection("physical", physicalInput)}
      {(eventType === "digital" || eventType === "both") &&
        renderSection("digital", digitalInput)}

      {/* Core Results */}
      <div className={`result ${isProfitable ? "success" : "fail"}`}>
        <h3>Total Projected Profit: ${metrics.profit.toFixed(2)}</h3>
        <p>Profitability Green Light: {isProfitable ? "✅ YES" : "❌ NO"}</p>
        <p>Total Revenue: ${metrics.totalRevenue.toFixed(2)}</p>
        <p>Total Cost: ${metrics.totalCost.toFixed(2)}</p>
        <p>
          Player-Driven: ${metrics.playerRevenue.toFixed(2)} | Marketing-Driven:{" "}
          ${metrics.marketingRevenue.toFixed(2)} | Recurring: $
          {metrics.recurringRevenue.toFixed(2)}
        </p>
        <p>Est. New Users: {Math.round(metrics.estNewUsers)}*</p>
        <p>
          Break-Even Events: {Math.round(Math.max(...metrics.breakEvenEvents))}{" "}
          events
        </p>
        <p>
          Cost Efficiency:{" "}
          {metrics.costEfficiencies.map((c, i) => (
            <span key={i}>{c.toFixed(2)}× </span>
          ))}
        </p>
        <p style={{ fontSize: "0.9em", color: "gray" }}>* Approximated</p>
      </div>

      {/* Forecast Panels */}
      {renderForecast()}
      {showEventForecast && renderEventForecast()}

      {/* Charts */}
      <RevenueBreakdownChart />
      {showEventForecast && <ForecastBarChart />}
      {showRiskSim && riskResults.summary && (
        <div className="risk-panel">
          <h3>🔀 Risk Simulation</h3>
          <p>Mean Profit: ${riskResults.summary.mean.toFixed(2)}</p>
          <p>Std Dev: ${riskResults.summary.std.toFixed(2)}</p>
          <p>Prob. of Loss: {riskResults.summary.probLoss.toFixed(1)}%</p>
          <div className="chart-wrapper">
            <h4>Profit Distribution</h4>
            <Bar
              data={{
                labels: (() => {
                  const pr = riskResults.profits;
                  const min = Math.min(...pr);
                  const max = Math.max(...pr);
                  const bins = 20;
                  const step = (max - min) / bins;
                  return Array.from({ length: bins }, (_, i) =>
                    (min + i * step).toFixed(0)
                  );
                })(),
                datasets: [
                  {
                    label: "Frequency",
                    data: (() => {
                      const pr = riskResults.profits;
                      const min = Math.min(...pr);
                      const max = Math.max(...pr);
                      const bins = 20;
                      const step = (max - min) / bins;
                      const counts = Array(bins).fill(0);
                      pr.forEach((p) => {
                        const idx = Math.min(
                          Math.floor((p - min) / step),
                          bins - 1
                        );
                        counts[idx]++;
                      });
                      return counts;
                    })(),
                  },
                ],
              }}
              options={{
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `${ctx.parsed.y} runs in this profit bin`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Scenario Comparison */}
      {comparisonResults && (
        <div className="comparison-panel">
          <h3>🔍 Scenario Comparison</h3>
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>{comparisonResults.a.name}</th>
                <th>{comparisonResults.b.name}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Total Cost", "totalCost"],
                ["Total Revenue", "totalRevenue"],
                ["Profit", "profit"],
                ["Player Rev", "playerRevenue"],
                ["Marketing Rev", "marketingRevenue"],
                ["Recurring Rev", "recurringRevenue"],
                ["Est. New Users", "estNewUsers"],
                [
                  "Break-Even",
                  (m) => Math.round(Math.max(...m.breakEvenEvents)),
                ],
                [
                  "Cost Eff.",
                  (m) => (m.totalRevenue / m.totalCost).toFixed(2) + "×",
                ],
              ].map(([label, key]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>
                    {typeof key === "function"
                      ? key(comparisonResults.a.metrics)
                      : comparisonResults.a.metrics[key].toFixed(2)}
                  </td>
                  <td>
                    {typeof key === "function"
                      ? key(comparisonResults.b.metrics)
                      : comparisonResults.b.metrics[key].toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sensitivity & Timeline Charts */}
      {showSensitivity && <TornadoChart />}
      {showTimeline && <TimelineChart />}
    </div>
  );
}
